import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import externalConversationsService, { 
  type ExternalConversation, 
  type ExternalConversationMessage,
  type CreateExternalConversationRequest,
  type SendMessageRequest
} from '@/services/external-conversations.service';

// External conversations by ticket ID
export const useExternalConversationsByTicket = (ticketId: string) => {
  return useQuery({
    queryKey: ['external-conversations', 'ticket', ticketId],
    queryFn: () => externalConversationsService.getByTicketId(ticketId),
    enabled: !!ticketId,
  });
};

// Single external conversation
export const useExternalConversation = (conversationId: string) => {
  return useQuery({
    queryKey: ['external-conversations', conversationId],
    queryFn: () => externalConversationsService.getById(conversationId),
    enabled: !!conversationId,
  });
};

// Messages for external conversation
export const useExternalConversationMessages = (conversationId: string) => {
  return useQuery({
    queryKey: ['external-conversations', conversationId, 'messages'],
    queryFn: () => externalConversationsService.getMessages(conversationId),
    enabled: !!conversationId,
  });
};

// Create external conversation mutation
export const useCreateExternalConversation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateExternalConversationRequest) => 
      externalConversationsService.create(data),
    onSuccess: (newConversation) => {
      // Invalidate and refetch external conversations for the ticket
      queryClient.invalidateQueries({
        queryKey: ['external-conversations', 'ticket', newConversation.ticket_id]
      });
      
      // Add the new conversation to the cache
      queryClient.setQueryData(
        ['external-conversations', newConversation.id],
        newConversation
      );
    },
  });
};

// Update external conversation mutation
export const useUpdateExternalConversation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ conversationId, data }: { 
      conversationId: string; 
      data: Partial<CreateExternalConversationRequest> 
    }) => externalConversationsService.update(conversationId, data),
    onSuccess: (updatedConversation) => {
      // Update the conversation in cache
      queryClient.setQueryData(
        ['external-conversations', updatedConversation.id],
        updatedConversation
      );
      
      // Invalidate ticket conversations list to reflect changes
      queryClient.invalidateQueries({
        queryKey: ['external-conversations', 'ticket', updatedConversation.ticket_id]
      });
    },
  });
};

// Delete external conversation mutation
export const useDeleteExternalConversation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (conversationId: string) => 
      externalConversationsService.delete(conversationId),
    onSuccess: (_, conversationId) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: ['external-conversations', conversationId]
      });
      
      // Invalidate all external conversations queries to refresh lists
      queryClient.invalidateQueries({
        queryKey: ['external-conversations']
      });
    },
  });
};

// Send message mutation
export const useSendExternalMessage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ conversationId, data }: { 
      conversationId: string; 
      data: SendMessageRequest 
    }) => externalConversationsService.sendMessage(conversationId, data),
    onSuccess: (newMessage, variables) => {
      const { conversationId } = variables;
      
      // Update messages cache with optimistic update
      queryClient.setQueryData(
        ['external-conversations', conversationId, 'messages'],
        (oldMessages: ExternalConversationMessage[] | undefined) => {
          if (!oldMessages) return [newMessage];
          return [...oldMessages, newMessage];
        }
      );
      
      // Optionally invalidate to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ['external-conversations', conversationId, 'messages']
      });
    },
    onError: (error, variables) => {
      // On error, invalidate to reset any optimistic updates
      const { conversationId } = variables;
      queryClient.invalidateQueries({
        queryKey: ['external-conversations', conversationId, 'messages']
      });
    }
  });
};

// External conversation stats
export const useExternalConversationStats = (ticketId?: string) => {
  return useQuery({
    queryKey: ['external-conversations', 'stats', ticketId],
    queryFn: () => externalConversationsService.getStats(ticketId),
  });
};